/**
 * Strategy references, in the shape of a container image reference:
 *
 *     namespace/slug[:tag][@sha256:digest]
 *     alex/rsi-dip:v2@sha256:5dc8eb7c3130…
 *
 * - `namespace` is the owner's username (case-insensitive, shown in lower case);
 *   `strategyhive` is the reserved official namespace.
 * - `slug` names the strategy within its namespace and survives renames.
 * - `tag` is `v<N>` (a published version) or `latest`; omitted means `latest`.
 * - `digest` is the SHA-256 of the strategy's canonical spec, the same hash the
 *   ledger, Verify and the League use. It is immutable: a tag can be read as
 *   "whatever v2 is", a digest only as "exactly these rules".
 *
 * Pure parsing and formatting only; resolving a reference is the hosted API's job.
 * See docs/NAMING.md and docs/adr/0008-strategy-references.md.
 */
export const OFFICIAL_NAMESPACE = "strategyhive";
/** Usernames nobody can sign up with: they read as the platform speaking. */
export const RESERVED_NAMESPACES = [
    OFFICIAL_NAMESPACE,
    "admin",
    "administrator",
    "official",
    "library",
    "support",
    "system",
    "root",
    "staff",
    "moderator",
];
export const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/;
const NAMESPACE_RE = /^[A-Za-z0-9_]{3,24}$/;
const TAG_RE = /^(?:latest|v[1-9][0-9]{0,5})$/;
const DIGEST_RE = /^[a-f0-9]{8,64}$/;
/** Shortest digest prefix the API accepts; the UI shows 12. */
export const MIN_DIGEST = 8;
/** A name, made into a slug: lower case, words joined by single hyphens, at most 48 characters. */
export function slugify(name) {
    const slug = name
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 48)
        .replace(/-+$/g, "");
    return slug.length >= 2 ? slug : `strategy${slug ? `-${slug}` : ""}`;
}
export function isValidSlug(slug) {
    return slug.length >= 2 && SLUG_RE.test(slug);
}
export function isReservedNamespace(username) {
    return RESERVED_NAMESPACES.includes(username.toLowerCase());
}
/** `v3` for version 3. */
export function versionTag(version) {
    return `v${version}`;
}
/** The version number a `vN` tag names, or null for `latest`. */
export function tagVersion(tag) {
    return tag === "latest" ? null : Number(tag.slice(1));
}
/**
 * Parse `namespace/slug[:tag][@sha256:hex]`. Returns null for anything malformed,
 * so callers answer 404 rather than guessing. `sha256:` may be omitted before the digest.
 */
export function parseRef(input) {
    const text = input.trim();
    const at = text.indexOf("@");
    const name = at === -1 ? text : text.slice(0, at);
    let digest;
    if (at !== -1) {
        digest = text
            .slice(at + 1)
            .replace(/^sha256:/i, "")
            .toLowerCase();
        if (!DIGEST_RE.test(digest))
            return null;
    }
    const slash = name.indexOf("/");
    if (slash === -1 || name.indexOf("/", slash + 1) !== -1)
        return null;
    const namespace = name.slice(0, slash);
    const rest = name.slice(slash + 1);
    const colon = rest.indexOf(":");
    const slug = (colon === -1 ? rest : rest.slice(0, colon)).toLowerCase();
    const tag = colon === -1 ? "latest" : rest.slice(colon + 1).toLowerCase();
    if (!NAMESPACE_RE.test(namespace) || !isValidSlug(slug) || !TAG_RE.test(tag))
        return null;
    return { namespace: namespace.toLowerCase(), slug, tag, ...(digest ? { digest } : {}) };
}
/** Format a reference. `digestLength` shortens the digest for display (0 leaves it out). */
export function formatRef(ref, digestLength = 64) {
    const tag = ref.tag ?? (ref.version ? versionTag(ref.version) : undefined);
    let out = `${ref.namespace.toLowerCase()}/${ref.slug}`;
    if (tag && tag !== "latest")
        out += `:${tag}`;
    if (ref.digest && digestLength > 0)
        out += `@sha256:${ref.digest.slice(0, digestLength)}`;
    return out;
}
/** Does a stored full digest match a (possibly shortened) digest from a reference? */
export function digestMatches(full, given) {
    return given.length >= MIN_DIGEST && full.toLowerCase().startsWith(given.toLowerCase());
}
//# sourceMappingURL=ref.js.map